import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IonicModule } from '@ionic/angular';
import { PageLayoutComponent, TitileNavbarComponent } from '@ui-components';
import {
  ENV_CONFIG,
  EnvConfig,
  FIRESTORE_TOKEN,
  FirestoreProvider,
  MODAL_TOKEN,
  ModalProvider,
  REMOTE_CONFIG_TOKEN,
  RemoteConfigKeys,
  RemoteConfigProvider,
  RemoteConfigValueType,
  USER_DATA_TOKEN,
  UserDataProvider,
  UserModel
} from '@core';
import { FeatureFlagDto } from '@feature-flags/infra';
import { from, switchMap, take, tap } from 'rxjs';
import { FeatureFlagConfirmModalComponent } from './feature-flag-confirm-modal/feature-flag-confirm-modal.component';

interface FeatureFlagItem {
  id: string;
  name: string;
  isEnabled: boolean;
  users: string[];
}

@Component({
  selector: 'lib-feature-flags',
  templateUrl: './feature-flags.component.html',
  styleUrls: ['./feature-flags.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [IonicModule, TitileNavbarComponent, PageLayoutComponent]
})
export class FeatureFlagsComponent implements OnInit {
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly env: EnvConfig = inject(ENV_CONFIG);
  private readonly firestoreProvider: FirestoreProvider = inject(FIRESTORE_TOKEN);
  private readonly remoteConfigProvider: RemoteConfigProvider = inject(REMOTE_CONFIG_TOKEN);
  private readonly userDataProvider: UserDataProvider = inject(USER_DATA_TOKEN);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);

  readonly flags: WritableSignal<FeatureFlagItem[]> = signal<FeatureFlagItem[]>([]);
  readonly isLoading: WritableSignal<boolean> = signal<boolean>(true);
  readonly canToggle: WritableSignal<boolean> = signal<boolean>(false);

  ngOnInit(): void {
    this.loadFlags();
    this.checkAdminAccess();
  }

  private loadFlags(): void {
    this.firestoreProvider
      .collectionData<FeatureFlagDto & { id: string }>('feature-flags', { idField: 'id' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((flags: (FeatureFlagDto & { id: string })[]) => {
        const mappedFlags: FeatureFlagItem[] = flags.map((flag: FeatureFlagDto & { id: string }) => ({
          id: flag.id,
          name: flag.id,
          isEnabled: flag.isEnabled,
          users: flag.users || []
        }));
        this.flags.set(mappedFlags);
        this.isLoading.set(false);
      });
  }

  toggleFlag(flag: FeatureFlagItem): void {
    if (!this.canToggle()) {
      return;
    }

    const newValue: boolean = !flag.isEnabled;

    this.modalProvider.showModal$<void>({
      component: FeatureFlagConfirmModalComponent,
      componentProps: {
        flagName: flag.name,
        newValue,
        action: () => this.updateFlag(flag.id, newValue)
      },
      cssClass: 'present-modal'
    });
  }

  private updateFlag(flagId: string, newValue: boolean): void {
    this.firestoreProvider.updateDocument('feature-flags', flagId, { isEnabled: newValue }).then(() => {
      this.flags.update((flags: FeatureFlagItem[]) =>
        flags.map((f: FeatureFlagItem) => (f.id === flagId ? { ...f, isEnabled: newValue } : f))
      );
    });
  }

  getUsersCount(flag: FeatureFlagItem): string {
    if (!flag.users?.length) {
      return 'Wszyscy';
    }
    return `${flag.users.length} użytkowników`;
  }

  private checkAdminAccess(): void {
    if (!this.env.production) {
      this.canToggle.set(true);
      return;
    }

    this.userDataProvider.userData$
      .pipe(
        take(1),
        switchMap((user: UserModel) =>
          from(
            this.remoteConfigProvider.getValue<string[]>(
              RemoteConfigKeys.FEATURE_FLAGS_MANAGEMENT,
              RemoteConfigValueType.JSON,
              []
            )
          ).pipe(tap((approvedUsers: string[]) => this.canToggle.set(approvedUsers?.includes(user.userId))))
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
